import axios from 'axios';
import { myQredApi } from '../../_baseApi/myQredApi.base.api';
import { downloadFile, generatePdfHelper } from '~/helpers/generatePdf.helper';
import {
  FileResponseDto,
  GetActiveLoanRequestDto,
  GetActiveLoanResponseDto,
  GetDownloadInvoicesRequestDto,
  GetDownloadInvoicesResponseDto,
  GetLoanCurrentInvoiceRequestDto,
  GetLoanDocumentDownloadRequestDto,
  GetPresignedLoanDownloadInvoiceRequestDto,
  GetLoanInvoicesRequestDto,
  GetLoanInvoicesResponseDto,
  GetLoanStatementRequestDto,
  GetLoanStatementResponseDto,
  LoanRequestDto,
  PatchPaymentMethodRequestDto,
  PaymentStatusResponseDTO,
  PaymentStatusRequestDTO,
} from '~/store/apis/dtos/loan/loans.dto';
import { accessToken } from '~/store/apis/utils/accessToken';
import { generateParams } from '~/store/apis/utils/generateParams';
import { PaginationResponse } from '~/store/apis/types/Pagination';
import {
  getLoanActiveMapped,
  getLoanCurrentInvoiceMapped,
  getLoanInvoicesMapped,
  getLoanStatementMapped,
} from '~/store/apis/endpoints/myQredApi/loan/loans.mapper';
import {
  Invoice,
  CurrentInvoiceResponse,
  AllLoansCurrentInvoicesResponse,
  LoanInvoiceData,
} from '~/models/loans/invoice';
import { Statement } from '~/models/loans/statement';
import { GetCompanyLoansResponseDto } from '~/store/apis/dtos/companies/companies.dto';
import { getCompanyLoansMapped } from '~/store/apis/endpoints/myQredApi/companies/companies.mapper';
import { notifications } from '@qred/components-library';
import { Loan } from '~/models/loans/loan';

export const loansApi = myQredApi.injectEndpoints({
  endpoints: (builder) => ({
    getLoanActive: builder.query<Loan, GetActiveLoanRequestDto>({
      async queryFn({ companyId, market }, _api, _extraOptions, baseQuery) {
        const loansRes = await baseQuery({
          url: `/company/v1/companies/${companyId}/loans`,
        });

        if (loansRes.error) {
          return { error: loansRes.error };
        }

        const loans = loansRes.data as GetCompanyLoansResponseDto[];

        const mappedLoans = getCompanyLoansMapped(loans, market);

        const activeLoans = mappedLoans.filter((loan) => loan.isActive);
        const activeLoan = activeLoans[0];

        if (!activeLoan) {
          return { data: (null as unknown) as Loan };
        }

        const loanRes = await baseQuery({
          url: `/loan/v1/loans/${activeLoan.id}`,
        });

        if (loanRes.error) {
          if (loanRes.error.status !== 404) {
            notifications.show({
              state: 'critical',
              autoClose: false,
              message: 'Something went wrong',
            });
          }
          return { error: loanRes.error };
        }

        const loan = loanRes.data as GetActiveLoanResponseDto;

        if (!loan) {
          return { data: (null as unknown) as Loan };
        }

        const mappedLoan = getLoanActiveMapped(loan, market);

        return { data: mappedLoan };
      },
      providesTags: (_result, _error, arg) => [
        { type: 'ActiveLoan', id: arg.companyId },
      ],
    }),
    getActiveLoans: builder.query<Loan[], GetActiveLoanRequestDto>({
      async queryFn({ companyId, market }, _api, _extraOptions, baseQuery) {
        const loansRes = await baseQuery({
          url: `/company/v1/companies/${companyId}/loans`,
        });

        if (loansRes.error) {
          return { error: loansRes.error };
        }

        const loans = loansRes.data as GetCompanyLoansResponseDto[];
        const mappedLoans = getCompanyLoansMapped(loans, market);
        const activeLoans = mappedLoans.filter((loan) => loan.isActive);

        // Fetch detailed data for each active loan
        const detailedLoans = await Promise.all(
          activeLoans.map(async (loan) => {
            const loanRes = await baseQuery({
              url: `/loan/v1/loans/${loan.id}`,
            });

            if (loanRes.error) {
              return null;
            }

            return getLoanActiveMapped(
              loanRes.data as GetActiveLoanResponseDto,
              market
            );
          })
        );

        // Filter out any failed requests
        const validLoans = detailedLoans.filter(
          (loan): loan is Loan => loan !== null
        );

        return { data: validLoans };
      },
      providesTags: (_result, _error, arg) => [
        { type: 'ActiveLoans', id: arg.companyId },
      ],
    }),
    getLoanDownloadDocument: builder.query<
      any,
      GetLoanDocumentDownloadRequestDto
    >({
      async queryFn({ loanId, documentType, ...rest }) {
        const token = await accessToken.get();
        try {
          const res = await axios.get(
            `${
              import.meta.env.VITE_MYQRED_API_URL
            }/loan/v1/loans/${loanId}/download/${documentType}?${generateParams(
              rest
            )}`,
            {
              responseType: 'blob',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          downloadFile(`${documentType}.pdf`, res.data, 'application/pdf');
          return { data: res.data };
        } catch (error) {
          return { error: error };
        }
      },
    }),
    getLoanStatement: builder.query<Statement, GetLoanStatementRequestDto>({
      query: ({ market: _market, loanId, ...rest }) =>
        `/loan/v1/loans/${loanId}/statement?${generateParams(rest)}`,
      providesTags: (_result, _error, arg) => [
        { type: 'LoanStatement', id: arg.loanId },
      ],
      transformResponse: (response: GetLoanStatementResponseDto, _meta, arg) =>
        getLoanStatementMapped(response, arg.market),
    }),
    getLoanCurrentInvoice: builder.query<
      CurrentInvoiceResponse | undefined,
      GetLoanCurrentInvoiceRequestDto
    >({
      query: ({ market: _market, loanId, ...rest }) =>
        `/loan/v1/loans/${loanId}/invoices?${generateParams(rest)}`,
      providesTags: (_result, _error, arg) => [
        { type: 'LoanInvoices', id: arg.loanId },
      ],
      transformResponse: (
        response: PaginationResponse<GetLoanInvoicesResponseDto>,
        _meta,
        arg
      ) => getLoanCurrentInvoiceMapped(response, arg.market),
    }),
    getAllLoansCurrentInvoices: builder.query<
      AllLoansCurrentInvoicesResponse | undefined,
      Omit<GetLoanCurrentInvoiceRequestDto, 'loanId'> & { loanIds: number[] }
    >({
      async queryFn(
        { loanIds, market, ...rest },
        _api,
        _extraOptions,
        baseQuery
      ) {
        if (!loanIds || loanIds.length === 0) {
          return {
            data: {
              loanInvoices: [],
              hasOverdueInvoices: false,
              areAllInvoicesUpcoming: false,
            },
          };
        }

        const results = await Promise.all(
          loanIds.map(async (loanId) => {
            const response = await baseQuery({
              url: `/loan/v1/loans/${loanId}/invoices?${generateParams(rest)}`,
            });

            if (response.error) {
              return null;
            }

            return {
              loanId,
              data: response.data as PaginationResponse<GetLoanInvoicesResponseDto>,
            };
          })
        );

        const validResults = results.filter(
          (
            r
          ): r is {
            loanId: number;
            data: PaginationResponse<GetLoanInvoicesResponseDto>;
          } => r !== null
        );

        if (validResults.length === 0) {
          return {
            data: {
              loanInvoices: [],
              hasOverdueInvoices: false,
              areAllInvoicesUpcoming: false,
            },
          };
        }

        // Map each loan's invoices and maintain loan-invoice relationship
        const loanInvoices = validResults.flatMap((result) => {
          const mappedInvoice = getLoanCurrentInvoiceMapped(
            result.data,
            market
          );
          if (!mappedInvoice || mappedInvoice.invoices.length === 0) {
            return [];
          }

          // Add loanId to each invoice and create a LoanInvoiceData for each
          return mappedInvoice.invoices.map(
            (invoice): LoanInvoiceData => ({
              loanId: result.loanId,
              invoice: {
                ...invoice,
                loanId: result.loanId,
              },
              hasOverdueInvoices: mappedInvoice.hasOverdueInvoices ?? false,
              areAllInvoicesUpcoming:
                mappedInvoice.areAllInvoicesUpcoming ?? false,
            })
          );
        });

        if (loanInvoices.length === 0) {
          return {
            data: {
              loanInvoices: [],
              hasOverdueInvoices: false,
              areAllInvoicesUpcoming: false,
            },
          };
        }

        // Check if any loan has overdue invoices
        const hasOverdueInvoices = loanInvoices.some(
          (item) => item.hasOverdueInvoices
        );

        // Check if all loans have only upcoming invoices
        const areAllInvoicesUpcoming = loanInvoices.every(
          (item) => item.areAllInvoicesUpcoming
        );

        return {
          data: {
            loanInvoices,
            hasOverdueInvoices,
            areAllInvoicesUpcoming,
          },
        };
      },
      providesTags: (_result, _error, arg) =>
        arg.loanIds.map((loanId) => ({
          type: 'LoanInvoices' as const,
          id: loanId,
        })),
    }),
    getLoanInvoices: builder.query<
      PaginationResponse<Invoice>,
      GetLoanInvoicesRequestDto
    >({
      query: ({ market: _market, loanId, ...rest }) =>
        `/loan/v1/loans/${loanId}/invoices?${generateParams(rest)}`,
      providesTags: (_result, _error, arg) => [
        { type: 'LoanInvoices', id: arg.loanId },
      ],
      transformResponse: (
        response: PaginationResponse<GetLoanInvoicesResponseDto>,
        _meta,
        arg
      ) => getLoanInvoicesMapped(response, arg.market),
    }),
    getLoanAgreement: builder.query<unknown, LoanRequestDto>({
      async queryFn({ loanId }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/loan/v1/loans/${loanId}/loan-agreement`,
        });
        const data = result.data as FileResponseDto;

        try {
          const res = await axios.get(data.url, {
            responseType: 'blob',
          });
          downloadFile('credit-agreement.pdf', res.data, 'application/pdf');
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'LoanAgreement', id: arg.loanId },
      ],
    }),
    getLoanPaymentPlan: builder.query<unknown, LoanRequestDto>({
      async queryFn({ loanId }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/loan/v1/loans/${loanId}/loan-payment-plan`,
        });
        const data = result.data as FileResponseDto;
        try {
          const res = await axios.get(data.url, {
            responseType: 'blob',
          });

          downloadFile('payment-plan.pdf', res.data, 'application/pdf');
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'LoanPaymentPlan', id: arg.loanId },
      ],
    }),
    getLoanDownloadInvoices: builder.query<
      GetDownloadInvoicesResponseDto,
      GetDownloadInvoicesRequestDto
    >({
      async queryFn({ loanId, ...rest }, _api, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `/loan/v1/loans/${loanId}/download/invoices?${generateParams(
            rest
          )}`,
        });
        if (response.error) {
          return { error: response.error };
        }
        const data = response.data as GetDownloadInvoicesResponseDto;
        generatePdfHelper(
          data.content,
          'invoice',
          rest.activeCompanyName,
          data.format
        );

        return { data };
      },
    }),
    getPresignedLoanDownloadInvoice: builder.query<
      unknown,
      GetPresignedLoanDownloadInvoiceRequestDto
    >({
      async queryFn(
        { loanId, fileName, ...rest },
        _api,
        _extraOptions,
        baseQuery
      ) {
        try {
          const response = await baseQuery({
            url: `/loan/v1/loans/${loanId}/download/invoice?${generateParams(
              rest
            )}`,
          });

          const data = response.data as FileResponseDto;

          const res = await axios.get(data.url, {
            responseType: 'blob',
          });
          const _fileName = fileName || data.fileName;
          downloadFile(`${_fileName}.pdf`, res.data, 'application/pdf');
          return { data };
        } catch (error) {
          return { error };
        }
      },
    }),
    postAmoFreeMonth: builder.mutation<unknown, LoanRequestDto>({
      query: ({ loanId, ...rest }) => ({
        url: `/loan/v1/loans/${loanId}/amo-free-month`,
        method: 'POST',
        body: rest,
      }),
    }),
    invalidateAmortizationCache: builder.mutation<null, { loanId: number }>({
      queryFn: async () => {
        // To make manual invalidation of the cache
        return { data: null };
      },
      invalidatesTags: ['ActiveLoan', 'LoanInvoices'],
    }),
    updatePaymentMethod: builder.mutation<
      unknown,
      PatchPaymentMethodRequestDto
    >({
      query: ({ loanId, ...rest }) => ({
        url: `/loan/v1/loans/${loanId}/payment-method`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: ['ActiveLoan'],
    }),
    getLoanTerminationLetter: builder.query<unknown, LoanRequestDto>({
      async queryFn({ loanId }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/loan/v1/loans/${loanId}/loan-termination-letter`,
        });
        const data = result.data as FileResponseDto;
        try {
          const res = await axios.get(data.url, {
            responseType: 'blob',
          });
          downloadFile('termination-letter.pdf', res.data, 'application/pdf');
          return { data };
        } catch (error) {
          return { error };
        }
      },
    }),
    getPaymentStatus: builder.query<
      PaymentStatusResponseDTO,
      PaymentStatusRequestDTO
    >({
      async queryFn({ loanId, ...rest }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/loan/v1/loans/${loanId}/payment-status`,
          method: 'GET',
          params: rest,
        });
        if (result.error) {
          if (result.error.status === 500) {
            return { data: (null as unknown) as PaymentStatusResponseDTO };
          }
          return { error: result.error };
        }
        return { data: result.data as PaymentStatusResponseDTO };
      },
      providesTags: (_result, _error, arg) => [
        { type: 'PaymentStatus', id: arg.loanId },
      ],
    }),
  }),
});

export const {
  useLazyGetLoanDownloadDocumentQuery,
  useGetLoanActiveQuery,
  useGetActiveLoansQuery,
  useGetLoanInvoicesQuery,
  useGetLoanCurrentInvoiceQuery,
  useGetAllLoansCurrentInvoicesQuery,
  useLazyGetLoanInvoicesQuery,
  useLazyGetLoanStatementQuery,
  useLazyGetLoanAgreementQuery,
  useLazyGetLoanPaymentPlanQuery,
  useLazyGetLoanDownloadInvoicesQuery,
  usePostAmoFreeMonthMutation,
  useInvalidateAmortizationCacheMutation,
  useUpdatePaymentMethodMutation,
  useLazyGetLoanTerminationLetterQuery,
  useLazyGetPresignedLoanDownloadInvoiceQuery,
  useLazyGetPaymentStatusQuery,
} = loansApi;
